import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { getSdk, AllNavigationSetsQuery } from "services/datocms/generated";
import { createDatoClient } from "services/datocms";
import {
  CollectionsDocument,
  CollectionsQuery,
} from "services/graphql/generated";
import { nameToSlug } from "@pepdirect/v3/helpers/string";

/**
 * Extended navigation set type from AllNavigationSetsQuery
 */
export type NavigationSetWithFlag =
  AllNavigationSetsQuery["allNavigationSets"][0];

/**
 * Transforms Collections query data into AllNavigationSetsQuery structure
 */
function transformCollectionsToNavigationSets(
  collections: CollectionsQuery["collections"]
): NavigationSetWithFlag[] {
  return collections.map((collection) => {
    const slug = nameToSlug(collection.name);

    return {
      __typename: "NavigationSetRecord",
      id: slug,
      name: collection.name,
      availableTags: [],
      navigationHeader: {
        __typename: "NavigationRecord",
        navigationItem: [
          {
            __typename: "ProductCategoryRecord",
            title: collection.name,
            id: slug,
            slug: slug,
            categoryImage: null,
          },
        ],
      },
    };
  });
}

/**
 * Fetches navigation sets from Algolia (GraphQL Collections)
 * @returns Navigation sets or null if no collections found
 */
async function fetchFromAlgolia(
  gqlClient: ApolloClient<NormalizedCacheObject>
): Promise<NavigationSetWithFlag[] | null> {
  try {
    const { data } = await gqlClient.query<CollectionsQuery>({
      query: CollectionsDocument,
    });

    if (!data.collections || data.collections.length === 0) {
      return null;
    }

    return transformCollectionsToNavigationSets(data.collections);
  } catch (error) {
    console.error("[fetchNavigationSets] Algolia fetch failed:", error);
    return null;
  }
}

/**
 * Fetches navigation sets from DatoCMS
 * @returns Navigation sets from DatoCMS
 */
async function fetchFromDatoCMS(): Promise<NavigationSetWithFlag[]> {
  const { allNavigationSets } = await getSdk(
    createDatoClient()
  ).AllNavigationSets();

  return allNavigationSets.map((set) => ({ ...set }));
}

/**
 * Fetches navigation sets with automatic fallback:
 * 1. Tries Algolia (GraphQL Collections) first
 * 2. Falls back to DatoCMS if Algolia returns no results
 * @param gqlClient - Apollo Client instance for Algolia queries
 * @returns Navigation sets in AllNavigationSetsQuery format
 */
export async function fetchNavigationSets(
  gqlClient: ApolloClient<NormalizedCacheObject>
): Promise<NavigationSetWithFlag[]> {
  try {
    // Try Algolia first
    const algoliaResult = await fetchFromAlgolia(gqlClient);
    if (algoliaResult) {
      return algoliaResult;
    }

    // Fall back to DatoCMS
    return await fetchFromDatoCMS();
  } catch (error) {
    console.error(
      "[fetchNavigationSets] Error fetching navigation sets:",
      error
    );
    throw error;
  }
}
